import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { Budget, BudgetCreate } from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';

const BudgetEditNew: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Estados del formulario
    const [formData, setFormData] = useState<Partial<Budget>>({
    proyecto: '',
    cliente: '',
    responsable: '',
    tel_cliente: '',
    email_cliente: ''
  });
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Cargar datos del presupuesto
  useEffect(() => {
    if (id) {
      loadBudget();
    }
  }, [id]);

  const loadBudget = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const budget = await BudgetApiService.getBudget(parseInt(id));
      setFormData({
        proyecto: budget.proyecto || '',
        cliente: budget.cliente,
        responsable: budget.responsable || '',
        email_cliente: budget.email_cliente || '',
        tel_cliente: budget.tel_cliente || ''
      });
    } catch (err: any) {
      console.error('Error loading budget:', err);
      setError(err.response?.data?.detail || 'Error al cargar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.cliente?.trim()) {
      newErrors.cliente = 'El nombre del cliente es requerido';
    }
    
    if (formData.email_cliente && !/\S+@\S+\.\S+/.test(formData.email_cliente)) {
      newErrors.email_cliente = 'El email no tiene un formato válido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en los campos
  const handleChange = (field: keyof BudgetCreate) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm() || !id) {
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      await BudgetApiService.updateBudget(parseInt(id), formData);
      navigate(`/budgets/${id}`);
    } catch (err: any) {
      console.error('Error updating budget:', err);
      setError(err.response?.data?.detail || 'Error al actualizar el presupuesto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/budgets/${id}`)}
          sx={{ mb: 2 }}
        >
          Volver al Presupuesto
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Editar Presupuesto
        </Typography>
      </Box>

      <Card>
        <CardHeader title="Información del Cliente" />
        
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Nombre del cliente */}
              <TextField
                fullWidth
                label="Nombre del Cliente"
                value={formData.cliente}
                onChange={handleChange('cliente')}
                error={!!errors.cliente}
                helperText={errors.cliente}
                required
              />

              {/* Email y Teléfono en fila */}
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email_cliente}
                  onChange={handleChange('email_cliente')}
                  error={!!errors.email_cliente}
                  helperText={errors.email_cliente}
                />

                <TextField
                  fullWidth
                  label="Teléfono"
                  value={formData.tel_cliente}
                  onChange={handleChange('tel_cliente')}
                />
              </Box>

              {/* Error */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Botones */}
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/budgets/${id}`)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BudgetEditNew;
